import { formatAxiosError } from "@/_services/axios"
import consoleService from "@/_services/console"
import { getListsValues } from "@/_services/lists"
import { loc } from "@/_services/localization"
import { addNotification, addOops } from "@/_services/notification"
// import { isLocalhost } from "@/_services/serviceWorker"
import { getIsAdmin, getOptions, getUsername } from "@/_services/userConfiguration"
import { getAppVersion, localStorageKeys, personRoles } from "@/_services/utils"
import axios from "axios"
import { convertDateStringsToDates, toQueryString } from "basikon-common-utils"
import sassVars from "../_styles/sass-vars.module.scss"

const cacheName = "hyperfront-sw-cache"

// these include parameters must be kept in sync
// so that searching in the service worker cache the keys match
export const assetIncludes = "supplier,documents,persons"
export const committeeIncludes = "personsNames"
export const communicationIncludes = "personsNames"
export const contractsIncludes = "partnerName,salesName,assetsNames"
export const contractIncludes = "assets,persons,fundings,documents,collaterals,financingServices"
export const prospectOrPersonIncludes = "descendantsNames,documents"
export const scoreIncludes = "documents"

const state = {
  offlineStateUpdateFn: null,
  offlineSince: null,
  counter: null,
  history: null,
}

function registerOfflineStateUpdateFn(offlineStateUpdateFn, history) {
  state.offlineStateUpdateFn = offlineStateUpdateFn
  state.history = history
}

function isActivated() {
  return window.location.origin.startsWith("https://") && getOptions("offlineSupport") // || isLocalhost
}

function hasOfflineDocumentUpload() {
  return getOptions("offlineSupport")?.documentUpload
}

function hasOfflineDocumentEncryption() {
  return Boolean(getOptions("offlineSupport")?.documentEncryption)
}

function setOffline(_isOffline, force) {
  if (!force && !isActivated()) return // do nothing
  if (consoleService.showOfflineLog) console.log("setOffline()", _isOffline)
  if (_isOffline) {
    if (state.offlineSince) return // already offline
    state.offlineSince = new Date()
    if (typeof state.offlineStateUpdateFn === "function") state.offlineStateUpdateFn(state.offlineSince)
    addNotification("You are now offline and using cached data", "warning", "tc")
    if (!force) offlinePing()
  } else {
    if (!state.offlineSince) return // already online
    state.offlineSince = null
    if (typeof state.offlineStateUpdateFn === "function") state.offlineStateUpdateFn()
    offlineExecuteActions(true)
  }
}

function isOffline() {
  return state.offlineSince ? true : false
}

async function offlinePing() {
  if (isOffline()) {
    try {
      await axios.get("/api/core/ping")
      setOffline(false)
    } catch {} // eslint-disable-line
    setTimeout(offlinePing, 2000)
  }
}

function rewriteTmpRegistrationDeep(value, tmpRegistration, savedRegistration) {
  if (Array.isArray(value)) {
    value.forEach(item => rewriteTmpRegistrationDeep(item, tmpRegistration, savedRegistration))
  } else if (value && typeof value === "object") {
    for (const key of Object.keys(value)) {
      if (value[key] === tmpRegistration) {
        value[key] = savedRegistration
      } else {
        rewriteTmpRegistrationDeep(value[key], tmpRegistration, savedRegistration)
      }
    }
  }
}

function getNextRegistration() {
  state.counter = (state.counter || 0) + 1
  let registration = state.counter.toString()
  while (registration.length < 8) registration = "0" + registration
  console.log("---- offlineService: getNextRegistration() returns", "TMP" + registration)
  return "TMP" + registration
}

function offlineActionSortRank(action) {
  if (action.offlineActionType === "transition") return 3
  if (action.method === "patch") return 2
  if (action.key.startsWith("/api/financing/contracts")) return 1
  if (action.key.startsWith("/api/person/prospects") && isClientLeadProspect(action.data)) return 0.5
  return 0
}

function isClientLeadProspect(data) {
  const roles = data?.roles
  if (Array.isArray(roles)) return roles.includes("CLIENT")
  if (typeof roles === "string") return roles.split(",").includes("CLIENT")
  return false
}

function offlinePush(action) {
  const offlineActions = readLocalStorage()
  action.username = getUsername()

  switch (action.method) {
    // post is for contractPage, assets, persons, prospects, scores
    case "post": {
      if (action.offlineActionType === "transition") {
        action.key = `${action.url}#${action.data?.code || ""}`

        const index = offlineActions.findIndex(it => it.key === action.key)
        if (index >= 0) {
          offlineActions[index] = action
        } else {
          offlineActions.push(action)
        }

        break
      }

      // set tmp registration if none
      let registration = action.data.registration || action.data._id
      if (!registration) {
        registration = getNextRegistration()
        action.data.registration = registration
        action.data._id = registration
      }

      const [urlPath, urlQuery] = action.url.split("?")
      action.key = urlPath + "/" + registration + (urlQuery ? "?" + urlQuery : "")

      // keep only one action with same key (or _update will be wrong the second time)
      const index = offlineActions.findIndex(it => it.key === action.key)
      if (index >= 0) {
        offlineActions[index] = action
      } else {
        offlineActions.push(action)
      }

      // keep some flag for debug purpose
      //action.data._fromOfflineCache = true

      break
    }
    case "patch": {
      // patch is for tasks
      action.key = action.url
      offlineActions.push(action)

      break
    }
    default: {
      throw Error("offlineService: offlinePush(): invalid method", action.method)
    }
  }

  // sort actions to get contract at the end, so that tmp registration get updated if needed
  // this is required in special case when user saves a contract a first time while offline, then adds a new asset.
  // In that case this new asset would be after the contract that references it
  offlineActions.sort((a, b) => offlineActionSortRank(a) - offlineActionSortRank(b))

  writeLocalStorage(offlineActions)
  return { ...action.data }
}

async function offlineExecuteActions(messageFromWorker) {
  if (consoleService.showOfflineLog) console.log("offlineService: execute()")
  if (!isActivated()) return // do nothing

  let offlineActions = readLocalStorage()

  // remove any action with wrong username (just in case user switched at some point)
  offlineActions = offlineActions.filter(it => it.username === getUsername())

  const offlineActionsErrors = []
  const nbActions = offlineActions.length
  if (nbActions === 0) {
    if (messageFromWorker) addNotification("You are now online", "success", "tc")
  } else {
    const allConvertedTmpRegistration = []
    while (offlineActions.length > 0) {
      const offlineAction = offlineActions[0]
      console.log("---- offlineExecuteActions execute", offlineAction)

      const tmpRegistration = offlineAction.data.registration?.startsWith("TMP0000") ? offlineAction.data.registration : null
      if (tmpRegistration) {
        offlineAction.data.tmpRegistration = tmpRegistration
        delete offlineAction.data.registration
        delete offlineAction.data._id
      }

      let savedData
      try {
        savedData = (await axios(offlineAction)).data
      } catch (error) {
        if (error.response) {
          // server was reached but returned an error (probably out of date, or bug)
          offlineActions[0].error = error
          const offlineActionCopy = { ...offlineActions[0], error }
          offlineActionsErrors.push(offlineActionCopy)
          console.log("offlineExecuteActions() error", offlineActionCopy)
        } else {
          // server could not be reached (probably a network error caused by poor network)
          console.log("offlineExecuteActions() error", error)
          offlineActions[0].trials = (offlineActions[0].trials || 0) + 1
          addOops(error)
          setOffline(true)
          break
        }
      }

      const transitionErrors = offlineAction.offlineActionType === "transition" ? savedData?.transitionResult?.errors : null
      if (Array.isArray(transitionErrors) && transitionErrors.length > 0) {
        const error = { response: { data: { errors: transitionErrors } } }
        offlineActions[0].error = error
        offlineActionsErrors.push({ ...offlineActions[0], error })
        console.log("offlineExecuteActions() transition rejected", transitionErrors)
      }

      // patch following "foreign keys" if some registration has been allocated
      let key = offlineAction.key
      const savedRegistration = savedData?.registration
      if (tmpRegistration && savedRegistration) {
        allConvertedTmpRegistration.push({ tmpRegistration, savedRegistration })
        for (const remainingAction of offlineActions) {
          rewriteTmpRegistrationDeep(remainingAction.data, tmpRegistration, savedRegistration)

          if (remainingAction.url?.includes(tmpRegistration)) {
            remainingAction.url = remainingAction.url.split(tmpRegistration).join(savedRegistration)
          }
          if (remainingAction.key?.includes(tmpRegistration)) {
            remainingAction.key = remainingAction.key.split(tmpRegistration).join(savedRegistration)
          }
        }
        if (hasOfflineDocumentUpload()) {
          const db = await openDb()
          const records = await new Promise((resolve, reject) => {
            const request = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readonly").objectStore(OFFLINE_DOCUMENTS_STORE_NAME).getAll()
            request.onsuccess = () => resolve(request.result)
            request.onerror = () => reject(request.error)
          })

          for (const record of records) {
            if (!record.url?.includes(tmpRegistration)) continue

            record.url = record.url.split(tmpRegistration).join(savedRegistration)
            await new Promise((resolve, reject) => {
              const transaction = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readwrite")
              transaction.objectStore(OFFLINE_DOCUMENTS_STORE_NAME).put(record)
              transaction.oncomplete = resolve
              transaction.onerror = () => reject(transaction.error)
            })
          }
        }

        // reload key to update workbox cache with newly saved data
        key = key.substring(0, offlineAction.key?.lastIndexOf("/")) + "/" + savedRegistration
      } else if (offlineAction.offlineActionType === "transition") {
        key = offlineAction.url.replace(/\/transitions(\?.*)?$/, "")
      }

      try {
        await axios.get(key)
      } catch (error) {
        if (error.response) {
          offlineActions[0].error = error
          const offlineActionCopy = { ...offlineActions[0], error }
          offlineActionsErrors.push(offlineActionCopy)
          console.log("offlineExecuteActions() error", offlineActionCopy)
        }
      }

      // When there is a synchronization error (apart from being offline) with the server, the offline actions are lost here.
      // An example of such errors is when the entity workflow has checks that prevents saving the entity.
      // In offline mode they can be check when saving locally the entity, but when network connectivity is restored,
      // the temp data is rejected by the server.
      // Should we one day need to avoid that, we would have to store them somewhere.
      offlineActions.shift()
    }

    // notify user
    if (offlineActionsErrors.length === 0) {
      const action = { label: "Got it", callback: () => setTimeout(window.location.reload()) }
      addNotification("You are now online and server has been updated with your changes", "success", "tc", action)
    } else {
      let message = loc`Error occured while synchronizing your data to the server`
      const errorMessages = offlineActionsErrors.map(actionError => {
        const entityType = actionError.key.startsWith("/api/financing/contracts")
          ? loc`Contract`
          : actionError.key.startsWith("/api/asset/assets")
            ? loc`Asset`
            : actionError.key.startsWith("/api/person/prospects")
              ? loc`Prospect`
              : actionError.key.startsWith("/api/person/persons")
                ? loc`Person`
                : actionError.key.startsWith("/api/scoring/scores")
                  ? loc`Score`
                  : actionError.key.startsWith("/api/core/tasks")
                    ? loc`Task`
                    : loc`???`
        return (
          entityType +
          " " +
          (actionError.data.registration || actionError.data.tmpRegistration || actionError.data.entity?.registration) +
          " (" +
          formatAxiosError(actionError.error) +
          ")"
        )
      })
      message += errorMessages.map(it => "\n" + it)
      const action = { label: "Got it", callback: () => setTimeout(window.location.reload()) }
      addNotification(message, "error", "tc", action)

      // save bad records in another key
      let errorStorage = JSON.parse(localStorage.getItem(localStorageKeys.OFFLINE_MODE.STORAGE_ERRORS)) || []
      while (errorStorage.length > 5) errorStorage.splice(errorStorage.length - 1, 1)
      errorStorage = [offlineActions].concat(errorStorage)
      localStorage.setItem(localStorageKeys.OFFLINE_MODE.STORAGE_ERRORS, JSON.stringify(errorStorage))
      offlineActions = []
    }
    writeLocalStorage(offlineActions)

    const { history } = state
    console.log("---- offlineService current history is", history?.location.pathname)
    for (const it of allConvertedTmpRegistration) {
      const { tmpRegistration, savedRegistration } = it
      if (history?.location.pathname.endsWith("/" + tmpRegistration)) {
        const newHistory = history?.location.pathname.replace(tmpRegistration, savedRegistration)
        console.log("---- offlineService change history to", newHistory)
        history.replace(newHistory)
        break
      }
    }
  }
  if (hasOfflineDocumentUpload()) await executeOfflineDocumentUploads()
  preload()
}

/**
 * No need to await for most calls after the cache has been opened.
 */
async function preload() {
  if (isOffline()) return
  if (!isActivated()) return // do nothing
  console.log("offlineService: preload()")

  const currentAppVersion = getAppVersion()
  const prevAppVersion = localStorage.getItem(localStorageKeys.OFFLINE_MODE.STORAGE_VERSION)
  let force = false
  if (currentAppVersion !== prevAppVersion) {
    force = true
    localStorage.setItem(localStorageKeys.OFFLINE_MODE.STORAGE_VERSION, currentAppVersion)
  }

  const workboxCache = await window.caches.open(cacheName)

  const offlineSupportOptions = getOptions("offlineSupport")
  const {
    preloadContractsCount,
    preloadIcons,
    preloadProspects,
    preloadScores,
    preloadLists,
    preloadScripts,
    preloadUri,
    preloadComponents,
    preloadContracts,
    preloadPersons,
  } = offlineSupportOptions || {}

  // this makes sure these lists are available when offline
  getListsValues((Array.isArray(preloadLists) ? preloadLists : []).join(","), { storeForOfflineUse: true })

  // load URIs that don't update often only if not yet loaded
  for (const uri of Array.isArray(preloadUri) ? preloadUri : []) {
    if (!(await workboxCache.match(uri))) axios.get(uri).catch(() => {})
  }

  if (preloadScripts?.length) {
    for (const script of preloadScripts) {
      const uri = `/api/script/scripts/${script}`
      if (!(await workboxCache.match(uri))) axios.get(uri).catch(() => {})
    }
  }

  if (Array.isArray(preloadIcons) && preloadIcons?.length) {
    const icns = sassVars.icns.split(",")
    for (let i = 0; i < icns?.length; i++) {
      const icn = icns[i].trim()
      if (preloadIcons.includes(icn)) {
        axios.get(`/icons/${icn}.svg`)
      }
    }
  }

  // this is to avoid duplicate requests, thus accelerating the initial load
  const fetchedUrls = {}

  if (Number.isFinite(preloadContractsCount)) {
    axios.get("/api/person/persons")
    axios.get("/api/core/tasks")

    // preload first contracts and their data, if they have changed
    // the include param must be kept in sync with DealsOrContractsPage
    const contracts = (await axios.get(`/api/financing/contracts${toQueryString({ include: contractsIncludes })}`)).data
    let count = 0
    for (const contract of contracts) {
      if (count++ >= preloadContractsCount) break

      const contractKey = `/api/financing/contracts/${contract.registration}`
      const contractsUrl = `/api/financing/contracts/${contract.registration}${toQueryString({ include: contractIncludes })}`
      const cachedResponse = await workboxCache.match(contractsUrl)
      const cachedContract = cachedResponse ? await cachedResponse.json() : undefined
      const contractIsoDate = contract._updateDate?.toISOString()

      if (!force && cachedContract?._updateDate === contractIsoDate) {
        console.log(`offlineService: preload() contract is up to date: ${contract.registration}`)
        continue
      }

      console.log(
        `offlineService: preload() contract needs preloading: ${contract.registration} ${cachedContract?._updateDate || ""} ${contractIsoDate}`,
      )

      if (!fetchedUrls[contractsUrl]) {
        fetchedUrls[contractsUrl] = true
        axios.get(contractsUrl).catch(() => {})
      }

      const contractTransitionsUrl = `${contractKey}/transitions?type=${contract.type}`
      if (!fetchedUrls[contractTransitionsUrl]) {
        fetchedUrls[contractTransitionsUrl] = true
        axios.get(contractTransitionsUrl).catch(() => {})
      }

      const contractDocumentsUrl = `${contractKey}/documents`
      if (!fetchedUrls[contractDocumentsUrl]) {
        fetchedUrls[contractDocumentsUrl] = true
        axios.get(contractDocumentsUrl).catch(() => {})
      }

      for (const { personRegistration, role } of contract.persons) {
        if (!personRegistration) continue

        const personUrl = `/api/person/persons/${personRegistration}`
        const personTransitionsUrl = `/api/person/persons/${personRegistration}/transitions?type=I`

        if (!fetchedUrls[personUrl]) {
          fetchedUrls[personUrl] = true
          axios.get(personUrl).catch(() => {})
        }

        if (!fetchedUrls[personTransitionsUrl]) {
          fetchedUrls[personTransitionsUrl] = true
          axios.get(personTransitionsUrl).catch(() => {})
        }

        if (getIsAdmin()) {
          const userOfPersonUrl = `/api/core/users?person=${personRegistration}`
          if (!fetchedUrls[userOfPersonUrl]) {
            fetchedUrls[userOfPersonUrl] = true
            axios.get(userOfPersonUrl).catch(() => {})
          }
        }

        if (role === personRoles.PARTNER) {
          const agreementUrl = `/api/financing/agreements/current-for-person/${personRegistration}`
          if (!fetchedUrls[agreementUrl]) {
            fetchedUrls[agreementUrl] = true
            axios.get(agreementUrl).catch(() => {})
          }
        }
      }

      for (const { assetRegistration } of contract.assets) {
        if (!assetRegistration) continue

        const assetUrl = `/api/asset/assets/${assetRegistration}`
        if (!fetchedUrls[assetUrl]) {
          fetchedUrls[assetUrl] = true
          axios.get(assetUrl).catch(() => {})
        }
      }
    }
  }

  if (preloadContracts?.url) {
    const { url, projection, count: preloadContractsCount = 30, preloadDocuments } = preloadContracts
    try {
      const contracts = (await axios.get(url + (projection ? toQueryString({ projection }) : ""))).data

      let contractCount = 0
      for (const contract of contracts) {
        if (contractCount++ >= preloadContractsCount) break
        await preloadEntity({
          entity: contract,
          entityUrl: `/api/financing/contracts/${contract.registration}`,
          include: contractIncludes,
          fetchedUrls,
          preloadDocuments,
          documentTypeList: "contractDocumentType",
        })
      }
    } catch {
      //
    }
  }

  if (preloadProspects?.url) {
    const { url, projection, count: preloadProspectsCount = 30, preloadDocuments, prospectIncludeFields = [] } = preloadProspects
    try {
      const prospects = (await axios.get(url + (projection ? toQueryString({ projection }) : ""))).data

      let prospectCount = 0
      for (const prospect of prospects) {
        if (prospectCount++ >= preloadProspectsCount) break
        await preloadEntity({
          entity: prospect,
          entityUrl: `/api/person/prospects/${prospect.registration}`,
          include: prospectOrPersonIncludes,
          fetchedUrls,
          prospectIncludeFields,
          preloadDocuments,
          documentTypeList: "personDocumentType",
        })
      }
    } catch {
      //
    }
  }

  if (preloadPersons?.url) {
    const { url, projection, include, count: preloadPersonsCount = 30, preloadDocuments, preloadAscendants } = preloadPersons
    try {
      const persons = (await axios.get(url + (projection ? toQueryString({ projection }) : ""))).data

      let prospectCount = 0
      for (const person of persons) {
        if (prospectCount++ >= preloadPersonsCount) break
        await preloadEntity({
          entity: person,
          entityUrl: `/api/person/persons/${person.registration}`,
          include: include ?? prospectOrPersonIncludes,
          fetchedUrls,
          preloadDocuments,
          documentTypeList: "personDocumentType",
          preloadAscendants,
        })
      }
    } catch {
      //
    }
  }

  if (preloadScores?.url) {
    const { url, projection, count: preloadScoresCount = 20, preloadDocuments, prospectIncludeFields = [] } = preloadScores
    try {
      const scores = (await axios.get(url + (projection ? toQueryString({ projection }) : ""))).data

      let scoreCount = 0
      for (const score of scores) {
        if (scoreCount++ >= preloadScoresCount) break
        await preloadEntity({
          entity: score,
          entityUrl: `/api/scoring/scores/${score.registration}`,
          include: scoreIncludes,
          fetchedUrls,
          prospectIncludeFields,
          preloadDocuments,
        })
      }
    } catch {
      //
    }
  }

  for (const componentName of Array.isArray(preloadComponents) ? preloadComponents : []) {
    preloadableComponents[componentName]?.().catch(() => {})
  }

  if (hasOfflineDocumentUpload() && hasOfflineDocumentEncryption()) {
    preloadableComponents.PassphrasePromptModal().catch(() => {})
  }
}

const preloadableComponents = {
  PassphrasePromptModal: () => import("@/_components/PassphrasePromptModal"),
  BootstrapSwitch: () => import("@/_components/BootstrapSwitch"),
  FormInputRichText: () => import("@/_components/FormInputRichText"),
  ProspectsOrPersonsPage: () => import("@/person/ProspectsOrPersonsPage"),
  ProspectOrPersonPage: () => import("@/person/ProspectOrPersonPage"),
  ScorePage: () => import("@/score/ScorePage"),
  EntitiesPage: () => import("@/_pages/EntitiesPage"),
  NavsCard: () => import("@/_components/NavsCard"),
  Page: () => import("@/_pages/Page"),
  scripts: () => import("@/_services/scripts"),
  ContentComponent: () => import("@/_components/ContentComponent"),
  ChartistCard: () => import("@/_components/ChartistCard"),
  EChartCard: () => import("@/_components/EChartCard"),
  StatsCard: () => import("@/_components/StatsCard"),
  CatalogComponent: () => import("@/_components/CatalogComponent"),
  EntityDepreciationSchedules: () => import("@/_components/EntityDepreciationSchedules"),
  TaskModal: () => import("@/_components/TaskModal"),
  SheetCard: () => import("@/_components/SheetCard"),
  EntityExtension: () => import("@/_components/EntityExtension"),
  PersonsComponent: () => import("@/person/PersonsComponent"),
  RiskInformationCard: () => import("@/person/RiskInformationCard"),
  assetUtils: () => import("@/_services/assetUtils"),
  leafletUtils: () => import("@/_services/leafletUtils"),
  MapContent: () => import("@/_components/MapContent"),
  PaymentPlanGraph: () => import("@/financing/PaymentPlanGraph"),
  ContractQuotationComponent: () => import("@/financing/ContractQuotationComponent"),
  ContractElements: () => import("@/financing/ContractElements"),
  EntityFundingsCard: () => import("@/financing/EntityFundingsCard"),
  EntityPaymentPlan: () => import("@/financing/EntityPaymentPlan"),
  AssetComponent: () => import("@/asset/AssetComponent"),
  AssetEquipmentComponent: () => import("@/asset/AssetEquipmentComponent"),
  EntityAssetsComponent: () => import("@/asset/EntityAssetsComponent"),
  EntityCollaterals: () => import("@/asset/EntityCollaterals"),
  PersonHierarchyComponent: () => import("@/person/PersonHierarchyComponent"),
  PersonSubscriptions: () => import("@/person/PersonSubscriptions"),
  EntityCommunicationCard: () => import("@/_components/EntityCommunicationCard.jsx"),
}

async function preloadEntity({
  entity,
  entityUrl,
  include,
  fetchedUrls,
  prospectIncludeFields = [],
  preloadDocuments,
  documentTypeList,
  preloadAscendants,
}) {
  if (!entity.registration) return

  if (fetchedUrls[entityUrl]) return
  fetchedUrls[entityUrl] = true

  // include param must match ProspectOrPersonPage's getEntity() call, or the cached response
  // won't be found when the page requests the same registration while offline
  try {
    entity = (await axios.get(entityUrl + toQueryString({ include }))).data
  } catch {
    return
  }

  const entityTransitionsUrl = `${entityUrl}/transitions?type=${entity.type}`
  if (!fetchedUrls[entityTransitionsUrl]) {
    fetchedUrls[entityTransitionsUrl] = true
    axios.get(entityTransitionsUrl).catch(() => {})
  }

  if (preloadDocuments) {
    const entityDocumentsUrl = `${entityUrl}/documents`
    if (!fetchedUrls[entityDocumentsUrl]) {
      fetchedUrls[entityDocumentsUrl] = true
      axios.get(entityDocumentsUrl).catch(() => {})
    }

    if (documentTypeList) {
      const mandatoryDocumentsUrl = `${entityUrl}/check-mandatory-documents?listOnly=true&listName=${documentTypeList}`
      if (!fetchedUrls[mandatoryDocumentsUrl]) {
        fetchedUrls[mandatoryDocumentsUrl] = true
        axios.get(mandatoryDocumentsUrl).catch(() => {})
      }
    }
  }

  if (preloadAscendants) {
    const entityAscendantsUrl = `${entityUrl}/ascendants`
    if (!fetchedUrls[entityAscendantsUrl]) {
      fetchedUrls[entityAscendantsUrl] = true
      axios.get(entityAscendantsUrl).catch(() => {})
    }
  }

  for (const prospectIncludeRegistration of prospectIncludeFields.map(field => entity?.[field])) {
    if (!prospectIncludeRegistration) continue
    const prospectIncludeUrl = `/api/person/prospects/${prospectIncludeRegistration}`
    if (!fetchedUrls[prospectIncludeUrl]) {
      fetchedUrls[prospectIncludeUrl] = true
      axios.get(prospectIncludeUrl).catch(() => {})
    }
  }
}

function readLocalStorage() {
  return JSON.parse(localStorage.getItem(localStorageKeys.OFFLINE_MODE.STORAGE_NAME)) || []
}

function writeLocalStorage(actions) {
  return localStorage.setItem(localStorageKeys.OFFLINE_MODE.STORAGE_NAME, JSON.stringify(actions))
}

const DB_NAME = "hyperfront-db"
const OFFLINE_DOCUMENTS_STORE_NAME = "pendingUploads"

let dbPromise = null

function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1)
      request.onupgradeneeded = () => {
        request.result.createObjectStore(OFFLINE_DOCUMENTS_STORE_NAME, { keyPath: "id" })
      }
      request.onsuccess = () => {
        // another tab attempting to upgrade/delete the database would otherwise block forever on
        // this connection staying open -- release it and let the next call reopen a fresh one
        request.result.onversionchange = () => {
          request.result.close()
          dbPromise = null
        }
        resolve(request.result)
      }
      request.onerror = () => {
        dbPromise = null // allow a later call to retry instead of staying stuck on the failed promise
        reject(request.error)
      }
    })
  }
  return dbPromise
}

function toTemporaryDocument(record) {
  return {
    _id: record.fields._id || record.id,
    name: record.filename,
    type: record.fields.type,
    groupIndex: record.fields.groupIndex,
    _temporaryOfflineDocument: true,
  }
}

const OFFLINE_DOCUMENTS_SALT_RECORD_ID = "__offlineDocumentsSalt__"
let offlineDocumentsEncryptionKey = null // CryptoKey, in-memory only -- never persisted, lost on reload

async function promptForPassphrase({ title, message }) {
  const [{ default: React }, { createRoot }, { default: PassphrasePromptModal }, { MemoryRouter }] = await Promise.all([
    import("react"),
    import("react-dom/client"),
    import("@/_components/PassphrasePromptModal"),
    import("react-router-dom"),
  ])

  return new Promise(resolve => {
    const container = document.createElement("div")
    document.body.appendChild(container)
    const root = createRoot(container)

    const cleanup = () => {
      root.unmount()
      container.remove()
    }

    root.render(
      React.createElement(
        MemoryRouter,
        null,
        React.createElement(PassphrasePromptModal, {
          title,
          message,
          onConfirm: passphrase => {
            cleanup()
            resolve(passphrase)
          },
          onCancel: () => {
            cleanup()
            resolve(null)
          },
        }),
      ),
    )
  })
}

async function getOfflineDocumentsEncryptionKey(db) {
  if (offlineDocumentsEncryptionKey) return offlineDocumentsEncryptionKey

  const existing = await new Promise((resolve, reject) => {
    const request = db
      .transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readonly")
      .objectStore(OFFLINE_DOCUMENTS_STORE_NAME)
      .get(OFFLINE_DOCUMENTS_SALT_RECORD_ID)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
  if (existing) return existing.salt

  const salt = crypto.getRandomValues(new Uint8Array(16))
  await new Promise((resolve, reject) => {
    const transaction = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readwrite")
    transaction.objectStore(OFFLINE_DOCUMENTS_STORE_NAME).put({ id: OFFLINE_DOCUMENTS_SALT_RECORD_ID, salt })
    transaction.oncomplete = resolve
    transaction.onerror = () => reject(transaction.error)
  })
  const passphrase = await promptForPassphrase({
    title: "Offline documents passphrase",
    message: "Enter a passphrase to protect documents saved while offline. You'll need to enter it again to send them once back online.",
  })
  if (!passphrase) return null

  const passphraseKey = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"])
  offlineDocumentsEncryptionKey = await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 250000, hash: "SHA-256" },
    passphraseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  )
  return offlineDocumentsEncryptionKey
}

async function decryptFileFromOfflineStorage(key, ciphertext, iv) {
  return crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext)
}

async function storeTemporaryOfflineDocument({ url, file, filename, fields }) {
  const db = await openDb()
  const queueId = getNextRegistration()
  const encrypted = hasOfflineDocumentEncryption()

  let record
  if (encrypted) {
    const key = await getOfflineDocumentsEncryptionKey(db)
    if (!key) throw new Error("A passphrase is required to save documents for offline upload")

    const iv = crypto.getRandomValues(new Uint8Array(12))
    const plaintext = await file.arrayBuffer()
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext)
    record = { id: queueId, url, ciphertext, iv, filename, fields, username: getUsername(), encrypted: true }
  } else {
    record = { id: queueId, url, file, filename, fields, username: getUsername(), encrypted: false }
  }

  await new Promise((resolve, reject) => {
    const transaction = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readwrite")
    transaction.objectStore(OFFLINE_DOCUMENTS_STORE_NAME).put(record)
    transaction.oncomplete = resolve
    transaction.onerror = () => reject(transaction.error)
  })

  return toTemporaryDocument(record)
}

async function deleteTemporaryOfflineDocument(id) {
  const db = await openDb()
  await new Promise((resolve, reject) => {
    const transaction = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readwrite")
    transaction.objectStore(OFFLINE_DOCUMENTS_STORE_NAME).delete(id)
    transaction.oncomplete = resolve
    transaction.onerror = () => reject(transaction.error)
  })
}

async function downloadTemporaryOfflineDocument(id) {
  const db = await openDb()
  const record = await new Promise((resolve, reject) => {
    const request = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readonly").objectStore(OFFLINE_DOCUMENTS_STORE_NAME).get(id)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
  if (!record) return null

  if (!record.encrypted) return { blob: record.file, filename: record.filename }

  for (let attempt = 1; attempt <= 2; attempt++) {
    const key = await getOfflineDocumentsEncryptionKey(db)
    if (!key) return null // user cancelled the prompt

    try {
      const decrypted = await decryptFileFromOfflineStorage(key, record.ciphertext, record.iv)
      return { blob: new Blob([decrypted]), filename: record.filename }
    } catch {
      // wrong passphrase so forget it so the next attempt re-prompts
      offlineDocumentsEncryptionKey = null
    }
  }
  return null
}

async function executeOfflineDocumentUploads() {
  if (!hasOfflineDocumentUpload()) return

  const db = await openDb()
  const records = await new Promise((resolve, reject) => {
    const request = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readonly").objectStore(OFFLINE_DOCUMENTS_STORE_NAME).getAll()
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  const pendingRecords = records.filter(it => it.username === getUsername())

  for (const record of pendingRecords) {
    let fileBlob

    if (!record.encrypted) {
      fileBlob = record.file
    } else {
      let decrypted
      for (let attempt = 1; attempt <= 2 && !decrypted; attempt++) {
        const key = await getOfflineDocumentsEncryptionKey(db)
        if (!key) return // user cancelled the prompt

        try {
          decrypted = await decryptFileFromOfflineStorage(key, record.ciphertext, record.iv)
        } catch {
          // wrong passphrase so forget it so the next attempt re-prompts
          offlineDocumentsEncryptionKey = null
        }
      }
      if (!decrypted) {
        console.log("offlineService: executeOfflineDocumentUploads() wrong passphrase, leaving record queued", record.id)
        continue
      }
      fileBlob = new Blob([decrypted])
    }

    try {
      const formData = new FormData()
      formData.append("file", fileBlob, record.filename)
      for (const [fieldKey, value] of Object.entries(record.fields)) {
        if (value !== undefined) formData.append(fieldKey, value)
      }

      await axios.post(record.url, formData, { headers: { "Content-Type": "multipart/form-data" } })

      await new Promise((resolve, reject) => {
        const transaction = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readwrite")
        transaction.objectStore(OFFLINE_DOCUMENTS_STORE_NAME).delete(record.id)
        transaction.oncomplete = resolve
        transaction.onerror = () => reject(transaction.error)
      })
    } catch (error) {
      console.log("offlineService: executeOfflineDocumentUploads() error", error)
    }
  }
}

async function findCachedOfflineTransitions(serverRoute, status) {
  const workboxCache = await window.caches.open(cacheName)
  const requests = await workboxCache.keys()
  const transitionsByCode = {}

  for (const request of requests) {
    if (!request.url.includes(`${serverRoute}/`) || !request.url.includes("/transitions")) continue

    try {
      const response = await workboxCache.match(request)
      const transitions = (await response.json()) || []
      for (const transition of transitions) {
        if (
          transition.offline === true &&
          (!Array.isArray(transition.source) || transition.source.includes(status)) &&
          !transitionsByCode[transition.code]
        ) {
          transitionsByCode[transition.code] = transition
        }
      }
    } catch {
      // ignore malformed or unrelated cache entries
    }
  }

  return Object.values(transitionsByCode)
}

async function findCachedMandatoryDocuments(serverRoute, type, status, listName) {
  const workboxCache = await window.caches.open(cacheName)
  const requests = await workboxCache.keys()
  const mandatoryDocumentsUrlSuffix = `/check-mandatory-documents?listOnly=true&listName=${listName}`

  for (const request of requests) {
    if (!request.url.includes(serverRoute) || !request.url.includes(mandatoryDocumentsUrlSuffix)) continue

    try {
      const entityUrl = request.url.split(mandatoryDocumentsUrlSuffix)[0]
      const entityResponse = await workboxCache.match(entityUrl, { ignoreSearch: true })
      const entity = entityResponse && (await entityResponse.json())
      if (!entity || entity.type !== type || entity.status !== status) continue

      const response = await workboxCache.match(request)
      const mandatoryDocuments = response && (await response.json())
      if (mandatoryDocuments) return mandatoryDocuments
    } catch {
      // ignore malformed or unrelated cache entries, keep looking
    }
  }

  return null
}

// this API is to store in a secondary cache (e.g. on top of not the workbox cache)
// only entities that can be changed while offline should be saved through this API
async function axiosOrPush(offlineAction) {
  if (isOffline()) {
    return offlinePush(offlineAction)
  } else {
    try {
      return (await axios(offlineAction)).data
    } catch (error) {
      if (isActivated() && !error.response) {
        console.log("==== assume offline", offlineAction)
        setOffline(true)
        return offlinePush(offlineAction)
      } else {
        throw error
      }
    }
  }
}

// this API is not for "general" caching, general caching on GETs is done by workbox
// this API should be used only in coordination with axiosorPush(), only for entities that can be modified when offline
// (e.g. contract, contract persons, assets)
const listApiPathsWithOfflineCreateSupport = ["/api/financing/contracts", "/api/person/prospects", "/api/person/persons", "/api/scoring/scores"]

async function axiosGetWithCacheSupport(url, options) {
  if (isOffline()) {
    const offlineActions = readLocalStorage()

    // we want exact matches here to avoid paths like /api/financing/contracts/:contractId/documents
    const listApiPath = listApiPathsWithOfflineCreateSupport.find(path => url === path || url.startsWith(`${path}?`))
    const documentsUrlMatch = url.match(/^(.+\/documents)(\?.*)?$/)

    if (listApiPath) {
      // prepend any TMP entity of this type
      const workboxCache = await window.caches.open(cacheName)
      const cachedResponse = await workboxCache.match(url)
      if (cachedResponse) {
        let cachedEntities = await cachedResponse.json()
        for (const action of offlineActions) {
          if (action.key.startsWith(`${listApiPath}/TMP0000`)) {
            convertDateStringsToDates(action.data)
            cachedEntities = [action.data, ...cachedEntities]
          }
        }
        return { data: cachedEntities, headers: {} }
      }
    } else if (documentsUrlMatch && hasOfflineDocumentUpload()) {
      const workboxCache = await window.caches.open(cacheName)
      const cachedResponse = await workboxCache.match(url)
      const cachedDocuments = cachedResponse ? await cachedResponse.json() : []

      const db = await openDb()
      const records = await new Promise((resolve, reject) => {
        const request = db.transaction(OFFLINE_DOCUMENTS_STORE_NAME, "readonly").objectStore(OFFLINE_DOCUMENTS_STORE_NAME).getAll()
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })

      const pendingDocuments = records
        .filter(it => it.username === getUsername() && it.url?.startsWith(documentsUrlMatch[1]))
        .map(toTemporaryDocument)
      if (cachedResponse || pendingDocuments.length || documentsUrlMatch[1].includes("/TMP0000")) {
        return { data: [...cachedDocuments, ...pendingDocuments], headers: {} }
      }
    } else {
      // return last cached if any
      for (let i = offlineActions.length - 1; i >= 0; i--) {
        if (url.startsWith(offlineActions[i].key) && !offlineActions[i].trials) {
          convertDateStringsToDates(offlineActions[i].data)
          return { data: offlineActions[i].data, headers: {} }
        }
      }
    }
  }

  return axios.get(url, options)
}

export {
  axiosGetWithCacheSupport,
  axiosOrPush,
  deleteTemporaryOfflineDocument,
  downloadTemporaryOfflineDocument,
  findCachedMandatoryDocuments,
  findCachedOfflineTransitions,
  hasOfflineDocumentUpload,
  isOffline,
  offlineExecuteActions,
  readLocalStorage,
  registerOfflineStateUpdateFn,
  setOffline,
  storeTemporaryOfflineDocument,
}
